import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useUser } from '../../hooks/queries/useAuth';
import { canAccessProject } from '../../lib/project-limits';
import { Loader } from '../../components/ui';
import { ErrorBoundary } from '../ErrorBoundary';
import { logger } from '../../utils/logger';

interface ProjectAccessCheckProps {
  children: React.ReactNode;
}

// Fallback component for error handling
const AccessCheckErrorFallback = () => (
  <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 text-gray-300 p-6 text-center">
    <h2 className="text-xl font-bold mb-2">Error Checking Project Access</h2>
    <p>There was a problem verifying your access to this project.</p>
    <button
      onClick={() => window.location.reload()}
      className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
    >
      Try Again
    </button>
  </div>
);

/**
 * Component that checks if a user can access a specific project
 * Redirects to subscription page with explanation if access is denied
 */
export function ProjectAccessCheck({ children }: ProjectAccessCheckProps) {
  const { projectId } = useParams<{ projectId: string }>();
  const { data: user } = useUser();
  const navigate = useNavigate();
  const [accessState, setAccessState] = useState<'initial' | 'loading' | 'allowed' | 'denied'>(
    'initial'
  );
  const redirectedRef = React.useRef(false);
  const previousProjectId = React.useRef<string | null>(null);
  const accessResultCache = React.useRef<Record<string, boolean>>({});

  // Only run the full check when projectId changes, otherwise return a cached result
  useEffect(() => {
    let isMounted = true;

    // Handle the case when we already checked this project ID
    if (accessResultCache.current[projectId] === true) {
      setAccessState('allowed');
      return;
    }

    // If projectId changed, reset the redirect flag
    if (previousProjectId.current !== projectId) {
      redirectedRef.current = false;
      previousProjectId.current = projectId;
    }

    // Set initial loading state, but only if we don't have a cached result
    if (accessState === 'initial') {
      setAccessState('loading');
    }

    const checkAccess = async () => {
      // If we already have a cached positive result, use it
      if (accessResultCache.current[projectId] === true) {
        if (isMounted) {
          setAccessState('allowed');
        }
        return;
      }

      if (!user || !projectId) {
        if (isMounted) {
          setAccessState('denied');
        }
        return;
      }

      try {
        const allowed = await canAccessProject(user.id, projectId);

        // Cache the result for positive outcomes
        if (allowed) {
          accessResultCache.current[projectId] = true;
        }

        if (!isMounted) return;

        setAccessState(allowed ? 'allowed' : 'denied');

        if (!allowed && !redirectedRef.current) {
          redirectedRef.current = true;
          navigate('/subscription', {
            state: {
              reason: 'project_access_denied',
              message:
                'Your free plan only allows access to your first 3 projects. Upgrade to access all your projects.',
            },
          });
        }
      } catch (error) {
        logger.error('Error checking project access:', error);
        if (isMounted) {
          setAccessState('denied');
        }
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [user, projectId, navigate, accessState]);

  if (accessState === 'loading' || accessState === 'initial') {
    // Render a more minimal loading indicator inside the content area
    return (
      <div className="flex items-center justify-center h-full">
        <Loader color="text-blue-500" />
      </div>
    );
  }

  if (accessState === 'denied') {
    return null; // Will redirect via the effect
  }

  // Wrap children in ErrorBoundary to handle potential errors in the project components
  return <ErrorBoundary fallback={<AccessCheckErrorFallback />}>{children}</ErrorBoundary>;
}
